import { AxiosError } from 'axios';
import type { InputMask } from 'imask';
import { type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type DokobitRequestCodeResponse,
  type RequestCodeResponse,
  VerificationType,
} from '~/api/Customer';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { ResponseErrorCodes } from '~/components/ModalAuth/constants';
import type { OtpErrorBody } from '~/components/ModalAuth/interface';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import type { DokobitStatusCheckProps } from '~/components/ModalDokobitStatusCheck';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore, userStore } from '~/stores';

import {
  formatTime,
  isTestPhoneNumber,
  setTimeoutData,
} from './components/PhoneNumberForm/utils';

const TIMEOUT_AMOUNT_LIST = [10, 30, 60, 120];

const AUDKENNI_USER_NOT_FOUND_CODE = 6009;
const AUDKENNI_ERROR_TRANSACTION_CODE = 6005;
const ANOTHER_KENNITALA_CODE = 20000;

export const useModalAuthHeim = () => {
  const phoneNumber = useRef<string>('');
  const captchaWidgetIdRef = useRef<string | undefined>(undefined);

  const { isMobile } = useGlobal();
  const { closeModal, openModal } = useModal();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [type, setType] = useState<VerificationType>(VerificationType.MOBILE);
  const [personalCode, setPersonalCode] = useState('');
  const [phoneVal, setPhoneVal] = useState<string>(
    userStore.personalData.phone || company.config.defaultPhoneCode || '',
  );
  const [phoneValid, setPhoneValid] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [policyAgreement, setPolicyAgreement] = useState<boolean>(false);
  const [policyError, setPolicyError] = useState<boolean>(false);

  const [codeVal, setCodeVal] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isPendingCode, setIsPendingCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNativeAuthLoading, setIsNativeAuthLoading] =
    useState<boolean>(false);
  const [canRequestAgain, setCanRequestAgain] = useState(false);
  const [maxAttemptsThreshold, setMaxAttemptsThreshold] = useState(0);
  const [isDeletedUser, setIsDeletedUser] = useState(false);
  const [isDokobitCheckStep, setIsDokobitCheckStep] = useState(false);
  const [dokobitCheckData, setDokobitCheckData] =
    useState<Nullable<DokobitStatusCheckProps['dokobitCheckData']>>(null);
  const [dokobitError, setDokobitError] = useState<
    { title: Nullable<string>; body: Nullable<string> } | undefined
  >();
  const [isDokobitFailed, setIsDokobitFailed] = useState<boolean>(false);
  const [timeoutEnabled, setTimeoutEnabled] = useState<boolean>(false);

  const [isDokobitCheckBackConfirming, setIsDokobitCheckBackConfirming] =
    useState(false);

  const [cancelCount, setCancelCount] = useState<number>(0);

  const getCaptchaToken = () =>
    company.isEnabledCaptcha && !mainStore.isRN
      ? (captchaWidgetIdRef.current &&
          window.turnstile?.getResponse(captchaWidgetIdRef.current)) ||
        ''
      : company.envVariables.REACT_APP_PREDEFINED_CAPTCHA_TOKEN;

  const backHandler = () => {
    closeModal([REDIRECT_QUERY_NAME]);
  };

  const handleTimeoutTick = (milliseconds: number) => {
    setDokobitError({
      title: t('phrases:dokobitCheckCanceledTitle'),
      body: t('phrases:dokobitCheckCanceledBody', {
        time: formatTime(milliseconds, {
          secondsLabel: t('sec_other'),
          minutesLabel: t('min_other'),
        }),
      }),
    });
  };

  const handlePhoneValueChange = (val: string) => {
    setPhoneVal(val);
    phoneNumber.current = val;
    if (phoneError) {
      setPhoneError('');
    }
  };

  const handlePolicyChange = (checked: boolean) => {
    if (policyError) {
      setPolicyError(false);
    }
    setPolicyAgreement(checked);
  };

  const validateForm = () => {
    let isValid = true;
    setPhoneError('');

    if (!phoneVal.trim()) {
      setPhoneError(t('phrases:phoneNumberRequired'));
      isValid = false;
    } else if (!phoneValid) {
      setPhoneError(t('phrases:invalidPhoneFormat'));
      isValid = false;
    }

    if (!policyAgreement) {
      setPolicyError(true);
      isValid = false;
    }
    return isValid;
  };

  const requestCodeWithDokobit = async () => {
    setDokobitError(undefined);
    setIsLoading(true);
    try {
      const res = await userStore.requestAuthCode(
        phoneNumber.current,
        getCaptchaToken(),
        true,
        type,
        personalCode,
      );
      if ('isDeleted' in res && res.isDeleted) {
        setIsDeletedUser(true);
      }
      setCanRequestAgain(false);

      handleDokobitSuccess(res);
    } catch (error) {
      handleDokobitError(error);

      setIsDokobitFailed(true);
      setIsDokobitCheckStep(false);
      if (!isMobile) {
        const isTestNumber = isTestPhoneNumber(phoneNumber.current);
        setDokobitError({
          title: t('phrases:phoneNotFoundInDatabase'),
          body: isTestNumber
            ? t('phrases:phoneNotFoundDescription')
            : t('phrases:phoneNotFoundDescriptionNoOtp'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDokobitError = (error: unknown) => {
    if (
      !(error instanceof AxiosError) ||
      (error.response?.status !== 422 && error.response?.status !== 400)
    ) {
      return;
    }

    const errorCode = error.response?.data?.error_code;
    if (!errorCode) {
      return;
    }

    setDokobitError(
      [
        AUDKENNI_USER_NOT_FOUND_CODE,
        AUDKENNI_ERROR_TRANSACTION_CODE,
        ANOTHER_KENNITALA_CODE,
      ].includes(errorCode)
        ? {
            title: t(`errors:${errorCode}Title`),
            body: t(`errors:${errorCode}Body`),
          }
        : {
            title: t('phrases:phoneNotFoundInDatabase'),
            body: t('phrases:phoneNotFoundDescriptionNoOtp'),
          },
    );

    setIsDokobitFailed(true);
    if (type === VerificationType.AUDKENNI) {
      setStep(2);
    } else {
      setIsDokobitCheckStep(false);
    }

    throw error;
  };

  const requestCodeRegular = async (useDokobit: boolean = true) => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setIsNativeAuthLoading(true);
    try {
      const res = await userStore.requestAuthCode(
        phoneNumber.current,
        getCaptchaToken(),
        useDokobit,
        type,
        personalCode,
      );
      if ('isDeleted' in res && res.isDeleted) {
        setIsDeletedUser(true);
      }
      setIsPendingCode(true);
      setCanRequestAgain(false);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 429 &&
        !error.response?.data?.message
      ) {
        mainStore.pushAlert(
          'error',
          `<div>${t('errors:unknown')}<br>${t('phrases:supportLink')}</div>`,
        );
      }
    }
    setIsNativeAuthLoading(false);
  };

  const handleDokobitSuccess = (
    response: DokobitRequestCodeResponse | RequestCodeResponse | AxiosError,
  ) => {
    if ((response as AxiosError).isAxiosError) {
      return;
    }

    if (
      'method' in response &&
      response.method === 'dokobit' &&
      response.data
    ) {
      setDokobitCheckData({
        code: response.data.control_code,
        token: response.data.token,
      });
      return;
    }

    if ('body' in response) {
      setIsPendingCode(true);
      setIsDeletedUser(response.isDeleted ?? false);
      setCanRequestAgain(false);
    }
  };

  const handlePhoneValidate = (flag: boolean) => {
    setPhoneValid(flag);

    if (!flag) {
      setCodeVal('');
      setIsCodeValid(false);
      setIsPendingCode(false);
      setMaxAttemptsThreshold(0);
    } else {
      analyticsService.track(AnalyticsEvent.LOGIN_PHONE_ENTERED, {});
    }
  };

  const handleCodeChange = (val: string, mask: InputMask<any>) => {
    setCodeVal(val);
    setIsCodeValid(mask.masked.isComplete);
  };

  const handleSubmitNumber = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const widgetId = captchaWidgetIdRef.current;
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      window.turnstile.execute(widgetId);
    } else {
      const isValid = validateForm();

      if (!isValid) {
        return;
      }

      const isTestNumber = isTestPhoneNumber(phoneNumber.current);
      if (isTestNumber) {
        requestCodeWithDokobit();
      } else {
        setIsDokobitCheckStep(true);
      }
    }
  };

  const handleSubmitCode = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!phoneVal || !codeVal) {
      return;
    }
    setIsLoading(true);
    try {
      const data = await userStore.verifyAuthCode(
        phoneVal.replace('+', ''),
        codeVal,
        Boolean(isDeletedUser),
      );
      if ('customer' in data && !data.customer.email && data.previousEmail) {
        mainStore.pushAlert('error', t('errors:emailIsAlreadyUse'));
      }
      if ('customer' in data && !data.customer.date_of_birth) {
        openModal(
          ModalType.BirthDate,
          'modal-auth-heim',
          redirectNext ? { [REDIRECT_QUERY_NAME]: redirectNext } : undefined,
          undefined,
          true,
        );
      } else if (redirectNext) {
        navigate(redirectNext, { replace: true });
      }
    } catch (error) {
      const errorBody: OtpErrorBody =
        (error as AxiosError<OtpErrorBody>)?.response?.data ||
        ({} as OtpErrorBody);
      if (errorBody.code) {
        switch (errorBody.code) {
          case ResponseErrorCodes.InvalidOtp:
            mainStore.pushAlert('error', t('errors:invalidCodeError'));
            break;
          case ResponseErrorCodes.TryOtpLimitIsReached:
            mainStore.pushAlert(
              'error',
              t('errors:tryOtpLimitIsReached', {
                timer: errorBody.timeout ? ` in ${errorBody.timeout}s` : '',
              }),
            );
            setMaxAttemptsThreshold(errorBody.timeout ?? 0);
            setIsPendingCode(false);
            setCodeVal('');
            break;
          default:
            mainStore.pushAlert('error', t('errors:otpCodeError'));
        }
      } else {
        mainStore.pushAlert('error', t('errors:otpCodeError'));
      }
      error && console.error(error);
    }
    setIsLoading(false);
  };

  const handleConfirmCancel = () => {
    setDokobitCheckData(null);
    setIsDokobitCheckBackConfirming(false);
    setIsDokobitFailed(false);
    setIsPendingCode(false);
    setCodeVal('');
    setPhoneError('');
    setStep(type === VerificationType.MOBILE ? 1 : 2);

    if (type === VerificationType.AUDKENNI) {
      setDokobitError(undefined);
      return;
    }

    setIsDokobitCheckStep(false);

    setCancelCount((prev) => {
      if (prev === 0) {
        const timeInMilliseconds =
          (TIMEOUT_AMOUNT_LIST.at(prev) as number) * 1000;
        setTimeoutData(timeInMilliseconds);
        setDokobitError({
          title: 'phrases:dokobitCheckCanceledTitle',
          body: t('phrases:dokobitCheckCanceledBody', {
            time: formatTime(timeInMilliseconds, {
              secondsLabel: t('sec_other'),
              minutesLabel: t('min_other'),
            }),
          }),
        });
      }

      if (prev > 0) {
        const nextTimeAmount = TIMEOUT_AMOUNT_LIST[
          prev >= TIMEOUT_AMOUNT_LIST.length - 1
            ? TIMEOUT_AMOUNT_LIST.length - 1
            : prev
        ] as number;

        const inMilliseconds = nextTimeAmount * 1000;
        setTimeoutData(inMilliseconds);
        setDokobitError({
          title: 'phrases:dokobitCheckCanceledTitle',
          body: t('phrases:dokobitCheckCanceledBody', {
            time: formatTime(inMilliseconds, {
              secondsLabel: t('sec_other'),
              minutesLabel: t('min_other'),
            }),
          }),
        });
      }

      return prev + 1;
    });

    setTimeoutEnabled(true);
  };

  const handleDokobitCheckSuccess = () => {
    mainStore.pushAlert(
      'success',
      userStore.personalData.isAdult
        ? t('phrases:dokobitAuthSuccessAdult')
        : t('phrases:dokobitAuthSuccess'),
    );
    if (redirectNext) {
      navigate(redirectNext, { replace: true });
    }
  };

  const handleTimeoutEnd = () => {
    setCancelCount(0);
    setTimeoutEnabled(false);
    setDokobitError(undefined);
  };

  const handleSwitchToSmsVerification = () => {
    setIsDokobitCheckStep(false);
    setDokobitCheckData(null);
    setIsPendingCode(true);
  };

  const handleBackFromCodeForm = () => {
    setIsPendingCode(false);
    if (captchaWidgetIdRef.current) {
      window.turnstile?.reset(captchaWidgetIdRef.current);
    }
    setCodeVal('');
  };

  const handleTryAgainWithDokobit = () => {
    setDokobitError(undefined);

    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    const widgetId = captchaWidgetIdRef.current;
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      window.turnstile.execute(widgetId);
    } else {
      requestCodeWithDokobit();
    }
  };

  const handleTrySmsOtp = () => {
    setDokobitError(undefined);
    const widgetId = captchaWidgetIdRef.current;
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      window.turnstile.execute(widgetId);
    } else {
      requestCodeRegular();
    }
  };

  const handleDokobitConfirmCancel = () => {
    setIsDokobitCheckBackConfirming(true);
  };

  const handleChangeNumber = () => {
    setIsDokobitFailed(false);
    setDokobitError(undefined);
  };

  return {
    step,
    setStep,
    type,
    setType,
    personalCode,
    setPersonalCode,
    phoneVal,
    phoneValid,
    phoneError,
    policyAgreement,
    policyError,
    codeVal,
    isCodeValid,
    isPendingCode,
    isLoading,
    isNativeAuthLoading,
    canRequestAgain,
    isDeletedUser,
    isDokobitCheckStep,
    dokobitCheckData,
    dokobitError,
    isDokobitFailed,
    timeoutEnabled,
    cancelCount,
    maxAttemptsThreshold,
    isDokobitCheckBackConfirming,
    setPhoneVal,
    setPhoneValid,
    setPhoneError,
    setPolicyAgreement,
    setPolicyError,
    setCodeVal,
    setIsCodeValid,
    setIsPendingCode,
    setIsLoading,
    setIsNativeAuthLoading,
    setCanRequestAgain,
    setMaxAttemptsThreshold,
    setIsDeletedUser,
    setIsDokobitCheckStep,
    setDokobitCheckData,
    setDokobitError,
    setIsDokobitFailed,
    setTimeoutEnabled,
    setCancelCount,
    setIsDokobitCheckBackConfirming,
    phoneNumber,
    captchaWidgetIdRef,
    backHandler,
    handleTimeoutTick,
    handlePhoneValueChange,
    handlePolicyChange,
    validateForm,
    requestCodeWithDokobit,
    requestCodeRegular,
    handlePhoneValidate,
    handleCodeChange,
    handleSubmitNumber,
    handleSubmitCode,
    handleConfirmCancel,
    handleTimeoutEnd,
    getCaptchaToken,
    handleDokobitCheckSuccess,
    handleSwitchToSmsVerification,
    handleBackFromCodeForm,
    handleTryAgainWithDokobit,
    handleTrySmsOtp,
    handleDokobitConfirmCancel,
    handleChangeNumber,
    handleDokobitError,
  };
};
